import React, { useEffect, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import Icon, { IconName } from "src/components/Icon/Icon";
import Toast from "src/components/Toast/Toast";
import * as S from "src/constants/StringConstants";
import {
  clearGMEGSIError,
  fetchApplicationGMEGSIDetails,
  saveActiveGMESchools,
  saveActiveGSIGroups,
  saveGMESchool,
  saveGSIGroup,
} from "src/store/reducers/applicationGmeGsi";
import "./AddGMEGSI.scss";

const typeConfig = {
  gme: {
    title: S.APP_GME_DETAILS_BUTTON_ADD_SCHOOL,
    historyTitle: S.APP_ADD_GME_SCHOOLS_HISTORY_TITLE,
  },
  gsi: {
    title: S.APP_GSI_DETAILS_BUTTON_ADD_GSI_GROUP,
    historyTitle: S.APP_ADD_GSI_GROUPS_HISTORY_TITLE,
  },
};

const initialState = {
  availableItems: [],
  activeItems: [],
  selectedAvailable: null,
  selectedActive: null,
  filterAvailable: "",
  filterActive: "",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_AVAILABLE_ITEMS":
      return { ...state, availableItems: action.payload };
    case "SET_ACTIVE_ITEMS":
      return { ...state, activeItems: action.payload };
    case "SET_SELECTED_AVAILABLE":
      return { ...state, selectedAvailable: action.payload };
    case "SET_SELECTED_ACTIVE":
      return { ...state, selectedActive: action.payload };
    case "SET_FILTER_AVAILABLE":
      return { ...state, filterAvailable: action.payload };
    case "SET_FILTER_ACTIVE":
      return { ...state, filterActive: action.payload };
    case "ADD_NEW_ITEM":
      return { ...state, availableItems: [...state.availableItems, action.payload] };
    case "RESET_LIST":
      return { ...initialState, availableItems: state.availableItems };
    default:
      return state;
  }
};

const AddGMEGSI = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const reduxDispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type") || "gme";
  const { title, historyTitle } = typeConfig[type];
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { AvailableGMESchools, AvailableGSIGroups, SelectedGMESchools, SelectedGSIGroups, loading } = useSelector(
    (state: any) => state.applicationGmeGsi.gmeGsiDetails,
  );
  const errorMessage = useSelector((state: any) => state.applicationGmeGsi.gmeGsiDetails.errorMessage);
  const userId = useSelector((state: any) => state.auth.user.userID);

  useEffect(() => {
    reduxDispatch(clearGMEGSIError());
  }, [reduxDispatch]);

  useEffect(() => {
    if (errorMessage) {
      setToastOpen(true);
    } else {
      setToastOpen(false);
    }
  }, [errorMessage]);

  useEffect(() => {
    reduxDispatch(fetchApplicationGMEGSIDetails(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (!loading) {
      const availableData = type === "gme" ? AvailableGMESchools : AvailableGSIGroups;
      const activeData = type === "gme" ? SelectedGMESchools : SelectedGSIGroups;

      //  filter out available items that already exist in activeItems based on description
      const filteredAvailable = availableData.filter(
        item =>
          !activeData.some(
            activeItem => activeItem.description.trim().toLowerCase() === item.description.trim().toLowerCase(),
          ),
      );

      dispatch({
        type: "SET_AVAILABLE_ITEMS",
        payload: filteredAvailable,
      });

      dispatch({
        type: "SET_ACTIVE_ITEMS",
        payload: activeData,
      });
    }
  }, [AvailableGMESchools, AvailableGSIGroups, SelectedGMESchools, SelectedGSIGroups, type, loading]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleAddItem = async () => {
    const trimmedTitle = newItemTitle.trim();

    if (trimmedTitle) {
      if (type === "gme") {
        await reduxDispatch(saveGMESchool(userId, trimmedTitle));
      } else {
        await reduxDispatch(saveGSIGroup(userId, trimmedTitle));
      }

      await reduxDispatch(fetchApplicationGMEGSIDetails(id));

      setNewItemTitle("");
      setIsModalOpen(false);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  const handleSaveAndClose = async () => {
    if (type === "gme") {
      reduxDispatch(saveActiveGMESchools(+id, userId, state.activeItems));
    } else {
      reduxDispatch(saveActiveGSIGroups(+id, userId, state.activeItems));
    }

    history.goBack();
  };

  const handleAdd = () => {
    if (state.selectedAvailable) {
      const selectedItem = state.availableItems.find(item => item.gid === state.selectedAvailable.gid);

      if (selectedItem) {
        dispatch({
          type: "SET_ACTIVE_ITEMS",
          payload: [...state.activeItems, selectedItem],
        });

        dispatch({
          type: "SET_AVAILABLE_ITEMS",
          payload: state.availableItems.filter(item => item.gid !== selectedItem.gid),
        });

        dispatch({ type: "SET_SELECTED_AVAILABLE", payload: null });
      }
    }
  };

  const handleRemove = () => {
    if (state.selectedActive) {
      const selectedItem = state.activeItems.find(item => item.gid === state.selectedActive.gid);

      if (selectedItem) {
        dispatch({
          type: "SET_AVAILABLE_ITEMS",
          payload: [...state.availableItems, selectedItem],
        });

        dispatch({
          type: "SET_ACTIVE_ITEMS",
          payload: state.activeItems.filter(item => item.gid !== selectedItem.gid),
        });

        dispatch({ type: "SET_SELECTED_ACTIVE", payload: null });
      }
    }
  };

  const resetList = () => {
    const availableData = type === "gme" ? AvailableGMESchools : AvailableGSIGroups;

    dispatch({
      type: "SET_AVAILABLE_ITEMS",
      payload: availableData,
    });

    dispatch({ type: "SET_ACTIVE_ITEMS", payload: [] });
  };

  return (
    <div className="add-gme-gsi">
      <AppBar icon={IconName.Applications} title={title} />

      <div className="add-gme-gsi__inner-container">
        <div className="add-gme-gsi__content-wrapper">
          <div className="add-gme-gsi__column-container">
            <div className="add-gme-gsi__column-title">
              <h3>{historyTitle}</h3>
              <input
                type="text"
                placeholder="Filter"
                value={state.filterAvailable}
                onChange={e => dispatch({ type: "SET_FILTER_AVAILABLE", payload: e.target.value })}
              />
            </div>

            <div className="add-gme-gsi__column">
              <div className="add-gme-gsi__list">
                {state.availableItems
                  .filter(item => item.description.toLowerCase().includes(state.filterAvailable.toLowerCase()))
                  .map(item => (
                    <div
                      key={item.gid}
                      className={state.selectedAvailable?.gid === item.gid ? "selected" : ""}
                      onClick={() => dispatch({ type: "SET_SELECTED_AVAILABLE", payload: item })}>
                      {item.description}
                    </div>
                  ))}
              </div>

              <div className="add-gme-gsi__button-container">
                <button className="actions__action-button" onClick={handleOpenModal}>
                  {S.APP_GME_GSI_ADD_NEW_BUTTON_LABEL}
                </button>
              </div>
            </div>
          </div>

          <div className="actions">
            <button className="actions__action-button" onClick={handleAdd} disabled={!state.selectedAvailable}>
              {S.APP_GME_GSI_ADD_BUTTON_LABEL}
              <Icon name={IconName.ArrowRight} />
            </button>
            <button className="actions__action-button" onClick={handleRemove} disabled={!state.selectedActive}>
              <Icon name={IconName.ArrowLeft} />
              {S.APP_GME_GSI_REMOVE_BUTTON_LABEL}
            </button>
          </div>

          <div className="add-gme-gsi__column-container">
            <div className="add-gme-gsi__column-title">
              <h3>{S.APP_GME_GSI_ACTIVE_LIST_TITLE}</h3>
              <input
                type="text"
                placeholder="Filter"
                value={state.filterActive}
                onChange={e => dispatch({ type: "SET_FILTER_ACTIVE", payload: e.target.value })}
              />
            </div>

            <div className="add-gme-gsi__column">
              <div className="add-gme-gsi__list">
                {state.activeItems
                  .filter(item => item.description.toLowerCase().includes(state.filterActive.toLowerCase()))
                  .map(item => (
                    <div
                      key={item.gid}
                      className={state.selectedActive?.gid === item.gid ? "selected" : ""}
                      onClick={() => dispatch({ type: "SET_SELECTED_ACTIVE", payload: item })}>
                      {item.description}
                    </div>
                  ))}
              </div>

              <div className="add-gme-gsi__button-container">
                <button className="actions__action-button" onClick={resetList}>
                  {S.APP_GME_GSI_RESET_LIST_BUTTON_LABEL}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="app-builder__action_buttons">
          <button className="button-v2 button-v2__secondary" onClick={handleCancel}>
            {S.APP_GME_GSI_CANCEL_BUTTON_LABEL}
          </button>
          <button className="button-v2 button-v2__primary" onClick={handleSaveAndClose}>
            {S.APP_GME_GSI_SAVE_AND_CLOSE_BUTTON_LABEL}
          </button>
        </div>
      </div>

      <PromptModal
        open={isModalOpen}
        title={title}
        confirmText={S.APP_GME_GSI_MODAL_ADD_BUTTON}
        rejectText={S.APP_GME_GSI_MODAL_CANCEL_BUTTON}
        onReject={() => setIsModalOpen(false)}
        onConfirm={handleAddItem}
        confirmDisabled={!newItemTitle.trim()}>
        <input
          className="add-gme-gsi-modal-input"
          type="text"
          value={newItemTitle}
          onChange={e => setNewItemTitle(e.target.value)}
        />
      </PromptModal>

      <Toast message={errorMessage || ""} open={toastOpen} onClose={() => setToastOpen(false)} />
    </div>
  );
};

export default AddGMEGSI;
